import { PasskeyError } from '~/services/errors'

/**
 * What to tell the user when a passkey ceremony fails — or `null` to say
 * nothing at all.
 *
 * Ported from cynque-dashboard's `app/utils/webauthn-errors.ts`, with two
 * changes:
 *
 * 1. **It reads `err.name`, never `instanceof DOMException`.** Since
 *    @simplewebauthn/browser v10, `startAuthentication`/`startRegistration`
 *    re-throw the browser's DOMException wrapped in a `WebAuthnError` (an
 *    `Error`), keeping the original `name`. The dashboard's `instanceof` check
 *    therefore never matches what the library actually throws.
 * 2. **A cancel says nothing** (issue #132). The browser folds "the user
 *    cancelled", "it timed out" and "the user denied it" into one
 *    `NotAllowedError`; the user already knows they closed the sheet. The same
 *    goes for an aborted ceremony, which is how the pending autofill sign-in
 *    ends when the user clicks "Sign in with a passkey" instead.
 */
export function describeWebAuthnError(
  err: unknown,
  operation: 'create' | 'get'
): string | null {
  if (err instanceof PasskeyError) {
    return err.message
  }
  // Read both off the object rather than gating on `instanceof Error`: a
  // DOMException is not an Error in every environment (jsdom, for one).
  const { name, code } =
    err && typeof err === 'object'
      ? (err as { name?: unknown; code?: unknown })
      : { name: undefined, code: undefined }

  if (
    name === 'NotAllowedError' ||
    name === 'AbortError' ||
    code === 'ERROR_CEREMONY_ABORTED'
  ) {
    return null
  }
  if (
    name === 'InvalidStateError' ||
    code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED'
  ) {
    return 'This device already has a passkey for Cynque.'
  }
  if (name === 'SecurityError') {
    // The page's host is not the deployment's RP ID: a review app, or local
    // dev pointed at the staging api.
    return "Passkeys aren't available at this address."
  }
  if (name === 'NotSupportedError') {
    return "This browser or device doesn't support passkeys."
  }
  return operation === 'create'
    ? "The passkey couldn't be created. Please try again."
    : "The passkey couldn't be used. Please try again."
}
